import { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../../../components/owner/PageHeader';
import {
  ColumnFilterHeader,
  formatPhoneFilterValue,
  formatDateFilterValue,
  matchTextFilter,
  matchAmountFilter,
  useColumnFilters,
} from '../../../components/admin/ColumnFilterHeader';
import { matchSaleDateRange, matchSaleHistoryDateFilter } from '../../receptionist/sales/saleHistoryFilters';
import TrainerForm from './TrainerForm';
import { formatCurrency } from './trainerData';
import { fetchTrainers, saveTrainer, deleteTrainer } from '../../../services/receptionist/receptionistApi';
import { fetchBranchNames } from '../../../services/gym/catalogApi';
import { usePanelToast } from '../../../context/PanelToastContext';
import { instantLoadState } from '../../../utils/instantLoad';
import PrintActionsHeader from '../../../components/shared/PrintActionsHeader';
import {
  exportTrainerCsv,
  exportTrainerExcel,
  exportTrainerPdf,
  exportTrainers,
  printTrainerList,
} from './exportTrainers';
import '../../owner/ownerPages.css';
import '../../owner/subscriptions/subscriptions.css';
import '../../receptionist/members/members.css';
import '../adminListPages.css';
import './trainers.css';

function getTrainerStatusLabel(status) {
  return status === 'active' ? 'Active' : 'Inactive';
}

function getCertificationLabel(trainer) {
  return (trainer.certifications || []).length > 0
    ? (trainer.certifications || []).join(', ')
    : 'No certifications';
}

function getAssignedMembersLabel(trainer) {
  return (trainer.assignedMembers || []).length > 0
    ? (trainer.assignedMembers || []).join(', ')
    : 'No members assigned';
}

function StatusBadge({ status }) {
  const isActive = status === 'active';
  return (
    <span className={`owner-badge ${isActive ? 'owner-badge-success' : 'owner-badge-warning'}`}>
      {isActive ? 'Active' : 'Inactive'}
    </span>
  );
}

function TrainerManagement() {
  const toast = usePanelToast();
  const trainerBootstrap = instantLoadState('trainers');
  const [trainerList, setTrainerList] = useState(trainerBootstrap.initialData);
  const [branchOptions, setBranchOptions] = useState([]);
  const [loading, setLoading] = useState(trainerBootstrap.initialLoading);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [formKey, setFormKey] = useState(0);
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const {
    filters,
    updateFilter,
    openFilterKey,
    toggleFilter,
  } = useColumnFilters({
    name: '',
    specialization: '',
    branch: '',
    phone: '',
    salary: '',
    members: '',
    status: '',
  });

  const {
    filters: scheduleFilters,
    updateFilter: updateScheduleFilter,
    openFilterKey: scheduleOpenKey,
    toggleFilter: toggleScheduleFilter,
  } = useColumnFilters({
    name: '',
    branch: '',
    shift: '',
    workingDays: '',
    timeSlot: '',
    status: '',
  });

  const {
    filters: salaryFilters,
    updateFilter: updateSalaryFilter,
    openFilterKey: salaryOpenKey,
    toggleFilter: toggleSalaryFilter,
  } = useColumnFilters({
    name: '',
    branch: '',
    salary: '',
    status: '',
  });

  const {
    filters: certFilters,
    updateFilter: updateCertFilter,
    openFilterKey: certOpenKey,
    toggleFilter: toggleCertFilter,
  } = useColumnFilters({
    name: '',
    specialization: '',
    certifications: '',
  });

  const {
    filters: membersFilters,
    updateFilter: updateMembersFilter,
    openFilterKey: membersOpenKey,
    toggleFilter: toggleMembersFilter,
  } = useColumnFilters({
    name: '',
    branch: '',
    assignedMembers: '',
    count: '',
  });

  const {
    filters: reportFilters,
    updateFilter: updateReportFilter,
    openFilterKey: reportOpenKey,
    toggleFilter: toggleReportFilter,
  } = useColumnFilters({
    trainer: '',
    report: '',
    type: '',
    date: '',
  });

  const loadData = useCallback(async () => {
    try {
      setError('');
      if (!trainerList.length) setLoading(true);
      const [trainers, branches] = await Promise.all([fetchTrainers(), fetchBranchNames()]);
      setTrainerList(trainers);
      setBranchOptions(branches);
    } catch (err) {
      setError(err.message || 'Failed to load trainers');
    } finally {
      setLoading(false);
    }
  }, [trainerList.length]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredTrainers = useMemo(() => (
    trainerList.filter((trainer) => {
      const rowDate = trainer.createdAt || trainer.joinedDate || trainer.date || '';
      const dateOk = !rowDate || matchSaleDateRange(rowDate, rangeStart, rangeEnd);
      return (
        dateOk
        && matchTextFilter(trainer.name, filters.name)
        && matchTextFilter(trainer.specialization, filters.specialization)
        && matchTextFilter(trainer.branch, filters.branch)
        && matchTextFilter(trainer.phone, filters.phone)
        && matchAmountFilter(trainer.salary, filters.salary, formatCurrency)
        && matchTextFilter(String((trainer.assignedMembers || []).length), filters.members)
        && matchTextFilter(getTrainerStatusLabel(trainer.status), filters.status)
      );
    })
  ), [trainerList, filters, rangeStart, rangeEnd]);

  const filteredScheduleTrainers = useMemo(() => (
    filteredTrainers.filter((trainer) => (
      matchTextFilter(trainer.name, scheduleFilters.name)
      && matchTextFilter(trainer.branch, scheduleFilters.branch)
      && matchTextFilter(trainer.schedule?.shift, scheduleFilters.shift)
      && matchTextFilter(trainer.schedule?.workingDays, scheduleFilters.workingDays)
      && matchTextFilter(trainer.schedule?.timeSlot, scheduleFilters.timeSlot)
      && matchTextFilter(getTrainerStatusLabel(trainer.status), scheduleFilters.status)
    ))
  ), [filteredTrainers, scheduleFilters]);

  const filteredSalaryTrainers = useMemo(() => (
    trainerList.filter((trainer) => (
      matchTextFilter(trainer.name, salaryFilters.name)
      && matchTextFilter(trainer.branch, salaryFilters.branch)
      && matchAmountFilter(trainer.salary, salaryFilters.salary, formatCurrency)
      && matchTextFilter(getTrainerStatusLabel(trainer.status), salaryFilters.status)
    ))
  ), [trainerList, salaryFilters]);

  const filteredCertTrainers = useMemo(() => (
    trainerList.filter((trainer) => (
      matchTextFilter(trainer.name, certFilters.name)
      && matchTextFilter(trainer.specialization, certFilters.specialization)
      && matchTextFilter(getCertificationLabel(trainer), certFilters.certifications)
    ))
  ), [trainerList, certFilters]);

  const filteredMemberTrainers = useMemo(() => (
    trainerList.filter((trainer) => (
      matchTextFilter(trainer.name, membersFilters.name)
      && matchTextFilter(trainer.branch, membersFilters.branch)
      && matchTextFilter(getAssignedMembersLabel(trainer), membersFilters.assignedMembers)
      && matchTextFilter(String((trainer.assignedMembers || []).length), membersFilters.count)
    ))
  ), [trainerList, membersFilters]);

  const reportRows = useMemo(() => (
    trainerList.flatMap((trainer) => (
      (trainer.reports || []).length > 0
        ? (trainer.reports || []).map((report) => ({
            key: report.id,
            trainerName: trainer.name,
            title: report.title,
            type: report.type,
            date: report.date,
            isEmpty: false,
          }))
        : [{
            key: `empty-${trainer.id}`,
            trainerName: trainer.name,
            title: 'No reports yet',
            type: '—',
            date: '—',
            isEmpty: true,
          }]
    ))
  ), [trainerList]);

  const filteredReportRows = useMemo(() => (
    reportRows.filter((row) => (
      matchTextFilter(row.trainerName, reportFilters.trainer)
      && matchTextFilter(row.title, reportFilters.report)
      && matchTextFilter(row.type, reportFilters.type)
      && matchSaleHistoryDateFilter(row.date, reportFilters.date)
      && matchSaleDateRange(row.date, rangeStart, rangeEnd)
    ))
  ), [reportRows, reportFilters, rangeStart, rangeEnd]);

  const openAddModal = () => {
    setFormKey((k) => k + 1);
    setShowAddModal(true);
  };

  const closeAddModal = () => setShowAddModal(false);

  const handleAddSubmit = async (form) => {
    try {
      const created = await saveTrainer(form);
      setTrainerList((prev) => [...prev, created]);
      closeAddModal();
      toast.showAdded('Trainer');
    } catch (err) {
      setError(err.message || 'Failed to add trainer');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteTrainer(deleteTarget.id);
      setTrainerList((prev) => prev.filter((t) => t.id !== deleteTarget.id));
      setDeleteTarget(null);
      toast.showDeleted('Trainer');
    } catch (err) {
      setError(err.message || 'Failed to delete trainer');
    }
  };

  return (
    <div>
      <PageHeader
        title="Trainers"
        subtitle="Manage gym trainers, salaries, and assignments"
      >
        <button type="button" className="owner-btn owner-btn-primary" onClick={openAddModal}>
          Add Trainer
        </button>
      </PageHeader>

      <PrintActionsHeader
        startDate={rangeStart}
        endDate={rangeEnd}
        onStartDateChange={setRangeStart}
        onEndDateChange={setRangeEnd}
        onClearDates={() => { setRangeStart(''); setRangeEnd(''); }}
        onPrint={() => printTrainerList(filteredTrainers)}
        onCsv={() => exportTrainerCsv(filteredTrainers)}
        onPdf={() => exportTrainerPdf(filteredTrainers)}
        onExcel={() => exportTrainerExcel(filteredTrainers)}
        onExport={() => exportTrainers(filteredTrainers)}
        ariaLabel="Trainer print and export"
      />

      {error && <p className="owner-placeholder-text owner-mb-16">{error}</p>}
      {loading && <p className="owner-placeholder-text owner-mb-16">Loading trainers...</p>}

      <div className="owner-card rec-panel-card rec-member-list-card admin-list-table-card owner-mb-28">
        <div className="owner-table-wrap">
          <table className="owner-table rec-member-table admin-list-table">
            <thead>
              <tr>
                <ColumnFilterHeader
                  label="Trainer"
                  filterKey="name"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search name..."
                />
                <ColumnFilterHeader
                  label="Specialization"
                  filterKey="specialization"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search specialization..."
                />
                <ColumnFilterHeader
                  label="Branch"
                  filterKey="branch"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search branch..."
                />
                <ColumnFilterHeader
                  label="Phone"
                  filterKey="phone"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="e.g. 0300-1234567"
                  formatValue={formatPhoneFilterValue}
                />
                <ColumnFilterHeader
                  label="Salary"
                  filterKey="salary"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search amount..."
                />
                <ColumnFilterHeader
                  label="Members"
                  filterKey="members"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search members..."
                />
                <ColumnFilterHeader
                  label="Status"
                  filterKey="status"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search status..."
                />
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTrainers.length > 0 ? (
                filteredTrainers.map((trainer) => (
                  <tr key={trainer.id}>
                    <td>{trainer.name}</td>
                    <td>{trainer.specialization}</td>
                    <td>{trainer.branch}</td>
                    <td>{trainer.phone}</td>
                    <td className="trainer-salary-cell">{formatCurrency(trainer.salary)}</td>
                    <td>{(trainer.assignedMembers || []).length}</td>
                    <td><StatusBadge status={trainer.status} /></td>
                    <td>
                      <button
                        type="button"
                        className="owner-btn owner-btn-sm trainer-btn-danger"
                        onClick={() => setDeleteTarget(trainer)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="owner-placeholder-text" style={{ textAlign: 'center', padding: '24px' }}>
                    No trainers found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="owner-card rec-panel-card admin-list-table-card owner-mb-28">
        <h2 className="owner-card-title">Schedule</h2>
        <div className="owner-table-wrap">
          <table className="owner-table rec-member-table admin-list-table">
            <thead>
              <tr>
                <ColumnFilterHeader
                  label="Trainer"
                  filterKey="name"
                  openKey={scheduleOpenKey}
                  onToggle={toggleScheduleFilter}
                  filters={scheduleFilters}
                  onFilterChange={updateScheduleFilter}
                  placeholder="Search name..."
                />
                <ColumnFilterHeader
                  label="Branch"
                  filterKey="branch"
                  openKey={scheduleOpenKey}
                  onToggle={toggleScheduleFilter}
                  filters={scheduleFilters}
                  onFilterChange={updateScheduleFilter}
                  placeholder="Search branch..."
                />
                <ColumnFilterHeader
                  label="Shift"
                  filterKey="shift"
                  openKey={scheduleOpenKey}
                  onToggle={toggleScheduleFilter}
                  filters={scheduleFilters}
                  onFilterChange={updateScheduleFilter}
                  placeholder="Search shift..."
                />
                <ColumnFilterHeader
                  label="Working Days"
                  filterKey="workingDays"
                  openKey={scheduleOpenKey}
                  onToggle={toggleScheduleFilter}
                  filters={scheduleFilters}
                  onFilterChange={updateScheduleFilter}
                  placeholder="Search days..."
                />
                <ColumnFilterHeader
                  label="Time Slot"
                  filterKey="timeSlot"
                  openKey={scheduleOpenKey}
                  onToggle={toggleScheduleFilter}
                  filters={scheduleFilters}
                  onFilterChange={updateScheduleFilter}
                  placeholder="Search time..."
                />
                <ColumnFilterHeader
                  label="Status"
                  filterKey="status"
                  openKey={scheduleOpenKey}
                  onToggle={toggleScheduleFilter}
                  filters={scheduleFilters}
                  onFilterChange={updateScheduleFilter}
                  placeholder="Search status..."
                />
              </tr>
            </thead>
            <tbody>
              {filteredScheduleTrainers.length > 0 ? (
                filteredScheduleTrainers.map((trainer) => (
                  <tr key={`schedule-${trainer.id}`}>
                    <td>{trainer.name}</td>
                    <td>{trainer.branch}</td>
                    <td>{trainer.schedule?.shift || '—'}</td>
                    <td>{trainer.schedule?.workingDays || '—'}</td>
                    <td>{trainer.schedule?.timeSlot || '—'}</td>
                    <td><StatusBadge status={trainer.status} /></td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="owner-placeholder-text" style={{ textAlign: 'center', padding: '24px' }}>
                    No trainers found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="owner-card rec-panel-card admin-list-table-card owner-mb-28">
        <h2 className="owner-card-title">Salary</h2>
        <div className="owner-table-wrap">
          <table className="owner-table rec-member-table admin-list-table">
            <thead>
              <tr>
                <ColumnFilterHeader
                  label="Trainer"
                  filterKey="name"
                  openKey={salaryOpenKey}
                  onToggle={toggleSalaryFilter}
                  filters={salaryFilters}
                  onFilterChange={updateSalaryFilter}
                  placeholder="Search name..."
                />
                <ColumnFilterHeader
                  label="Branch"
                  filterKey="branch"
                  openKey={salaryOpenKey}
                  onToggle={toggleSalaryFilter}
                  filters={salaryFilters}
                  onFilterChange={updateSalaryFilter}
                  placeholder="Search branch..."
                />
                <ColumnFilterHeader
                  label="Monthly Salary"
                  filterKey="salary"
                  openKey={salaryOpenKey}
                  onToggle={toggleSalaryFilter}
                  filters={salaryFilters}
                  onFilterChange={updateSalaryFilter}
                  placeholder="Search amount..."
                />
                <ColumnFilterHeader
                  label="Status"
                  filterKey="status"
                  openKey={salaryOpenKey}
                  onToggle={toggleSalaryFilter}
                  filters={salaryFilters}
                  onFilterChange={updateSalaryFilter}
                  placeholder="Search status..."
                />
              </tr>
            </thead>
            <tbody>
              {filteredSalaryTrainers.map((trainer) => (
                <tr key={`salary-${trainer.id}`}>
                  <td>{trainer.name}</td>
                  <td>{trainer.branch}</td>
                  <td className="trainer-salary-cell">{formatCurrency(trainer.salary)}</td>
                  <td><StatusBadge status={trainer.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="owner-card rec-panel-card admin-list-table-card owner-mb-28">
        <h2 className="owner-card-title">Certifications</h2>
        <div className="owner-table-wrap">
          <table className="owner-table rec-member-table admin-list-table">
            <thead>
              <tr>
                <ColumnFilterHeader
                  label="Trainer"
                  filterKey="name"
                  openKey={certOpenKey}
                  onToggle={toggleCertFilter}
                  filters={certFilters}
                  onFilterChange={updateCertFilter}
                  placeholder="Search name..."
                />
                <ColumnFilterHeader
                  label="Specialization"
                  filterKey="specialization"
                  openKey={certOpenKey}
                  onToggle={toggleCertFilter}
                  filters={certFilters}
                  onFilterChange={updateCertFilter}
                  placeholder="Search specialization..."
                />
                <ColumnFilterHeader
                  label="Certifications"
                  filterKey="certifications"
                  openKey={certOpenKey}
                  onToggle={toggleCertFilter}
                  filters={certFilters}
                  onFilterChange={updateCertFilter}
                  placeholder="Search certifications..."
                />
              </tr>
            </thead>
            <tbody>
              {filteredCertTrainers.map((trainer) => (
                <tr key={`cert-${trainer.id}`}>
                  <td>{trainer.name}</td>
                  <td>{trainer.specialization}</td>
                  <td>
                    <div className="trainer-cert-list">
                      {(trainer.certifications || []).length > 0 ? (
                        (trainer.certifications || []).map((cert) => (
                          <span key={cert} className="trainer-cert-item">{cert}</span>
                        ))
                      ) : (
                        <span className="owner-placeholder-text">No certifications</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="owner-card rec-panel-card admin-list-table-card owner-mb-28">
        <h2 className="owner-card-title">Assigned Members</h2>
        <div className="owner-table-wrap">
          <table className="owner-table rec-member-table admin-list-table">
            <thead>
              <tr>
                <ColumnFilterHeader
                  label="Trainer"
                  filterKey="name"
                  openKey={membersOpenKey}
                  onToggle={toggleMembersFilter}
                  filters={membersFilters}
                  onFilterChange={updateMembersFilter}
                  placeholder="Search name..."
                />
                <ColumnFilterHeader
                  label="Branch"
                  filterKey="branch"
                  openKey={membersOpenKey}
                  onToggle={toggleMembersFilter}
                  filters={membersFilters}
                  onFilterChange={updateMembersFilter}
                  placeholder="Search branch..."
                />
                <ColumnFilterHeader
                  label="Assigned Members"
                  filterKey="assignedMembers"
                  openKey={membersOpenKey}
                  onToggle={toggleMembersFilter}
                  filters={membersFilters}
                  onFilterChange={updateMembersFilter}
                  placeholder="Search members..."
                />
                <ColumnFilterHeader
                  label="Count"
                  filterKey="count"
                  openKey={membersOpenKey}
                  onToggle={toggleMembersFilter}
                  filters={membersFilters}
                  onFilterChange={updateMembersFilter}
                  placeholder="Search count..."
                />
              </tr>
            </thead>
            <tbody>
              {filteredMemberTrainers.map((trainer) => (
                <tr key={`members-${trainer.id}`}>
                  <td>{trainer.name}</td>
                  <td>{trainer.branch}</td>
                  <td>
                    <div className="trainer-member-tags">
                      {(trainer.assignedMembers || []).length > 0 ? (
                        (trainer.assignedMembers || []).map((member) => (
                          <span key={member} className="trainer-member-tag">{member}</span>
                        ))
                      ) : (
                        <span className="owner-placeholder-text">No members assigned</span>
                      )}
                    </div>
                  </td>
                  <td>{(trainer.assignedMembers || []).length}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="owner-card rec-panel-card admin-list-table-card">
        <h2 className="owner-card-title">Reports</h2>
        <div className="owner-table-wrap">
          <table className="owner-table rec-member-table admin-list-table">
            <thead>
              <tr>
                <ColumnFilterHeader
                  label="Trainer"
                  filterKey="trainer"
                  openKey={reportOpenKey}
                  onToggle={toggleReportFilter}
                  filters={reportFilters}
                  onFilterChange={updateReportFilter}
                  placeholder="Search trainer..."
                />
                <ColumnFilterHeader
                  label="Report"
                  filterKey="report"
                  openKey={reportOpenKey}
                  onToggle={toggleReportFilter}
                  filters={reportFilters}
                  onFilterChange={updateReportFilter}
                  placeholder="Search report..."
                />
                <ColumnFilterHeader
                  label="Type"
                  filterKey="type"
                  openKey={reportOpenKey}
                  onToggle={toggleReportFilter}
                  filters={reportFilters}
                  onFilterChange={updateReportFilter}
                  placeholder="Search type..."
                />
                <ColumnFilterHeader
                  label="Date"
                  filterKey="date"
                  openKey={reportOpenKey}
                  onToggle={toggleReportFilter}
                  filters={reportFilters}
                  onFilterChange={updateReportFilter}
                  placeholder="DD-MM-YYYY"
                  formatValue={formatDateFilterValue}
                />
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredReportRows.map((row) => (
                row.isEmpty ? (
                  <tr key={row.key}>
                    <td>{row.trainerName}</td>
                    <td colSpan={3} className="owner-placeholder-text">No reports yet</td>
                    <td>—</td>
                  </tr>
                ) : (
                  <tr key={row.key}>
                    <td>{row.trainerName}</td>
                    <td>{row.title}</td>
                    <td>{row.type}</td>
                    <td>{row.date}</td>
                    <td>
                      <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm">
                        View Report
                      </button>
                    </td>
                  </tr>
                )
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showAddModal && (
        <div className="subscription-modal-overlay" onClick={closeAddModal}>
          <div
            className="subscription-modal admin-form-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-trainer-title"
          >
            <div className="subscription-modal-header">
              <h2 id="add-trainer-title" className="subscription-modal-title">Add Trainer</h2>
              <button type="button" className="subscription-modal-close" onClick={closeAddModal} aria-label="Close">×</button>
            </div>
            <TrainerForm
              key={formKey}
              branchOptions={branchOptions}
              submitLabel="Add Trainer"
              onSubmit={handleAddSubmit}
              onCancel={closeAddModal}
            />
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="subscription-modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="subscription-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="subscription-modal-header">
              <h2 className="subscription-modal-title">Delete Trainer</h2>
              <button type="button" className="subscription-modal-close" onClick={() => setDeleteTarget(null)} aria-label="Close">×</button>
            </div>
            <p className="trainer-modal-message">
              Delete trainer <strong>{deleteTarget.name}</strong>? This action cannot be undone.
            </p>
            <div className="subscription-form-actions">
              <button type="button" className="owner-btn trainer-btn-danger" onClick={handleDelete}>Delete Trainer</button>
              <button type="button" className="owner-btn owner-btn-secondary" onClick={() => setDeleteTarget(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TrainerManagement;
